"use client";

import { SerwistProvider } from "@serwist/turbopack/react";
import { ThemeProvider } from "@web/src/components/theme-provider";
import { BridgeProvider } from "@web/src/components/BridgeProvider";
import { stores } from "@web/src/stores/web-stores";
import "@web/src/styles/globals.css";
import { MobXProviderContext, observer } from "mobx-react";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useIosScrollFix } from "@web/src/hooks/useIosScrollFix";
import { usePersistRoute } from "@web/src/hooks/usePwaRoute";

const AppLayout = dynamic(() => import("@web/src/components/app-layout"), {
  ssr: false,
});

const App = observer(({ Component, pageProps }: AppProps) => {
  const { authStore, setlistStore, songLibraryStore } = stores;
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const isStandalonePage = router.pathname === "/";

  useEffect(() => {
    if (!isStandalonePage) return;
    if (!authStore.isHydrated) return;
    if (authStore.isAuthenticated) {
      routerRef.current.replace("/setlist");
    }
  }, [isStandalonePage, authStore.isHydrated, authStore.isAuthenticated]);

  /**
   * Load setlist and song libraries from IndexedDB cache.
   * The pull() call always loads from cache first, then attempts
   * a background sync (which may fail silently if offline).
   */
  useEffect(() => {
    if (!router.isReady) return;
    if (!authStore.isHydrated) return;
    if (!authStore.isAuthenticated) return;
    void setlistStore.library.pull();
    void songLibraryStore
      .loadAccessibleLibraries()
      .then(() => songLibraryStore.syncLibrary())
      .catch((err) => console.error("Failed to sync song library:", err));
  }, [
    router.isReady,
    authStore.isHydrated,
    authStore.isAuthenticated,
    setlistStore,
    songLibraryStore,
  ]);

  /**
   * Re-sync setlist library when the tab regains focus
   * to pick up changes from other devices/tabs.
   */
  useEffect(() => {
    if (!authStore.isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      void setlistStore.library.sync();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [authStore.isAuthenticated, setlistStore]);

  usePersistRoute();
  useIosScrollFix();

  /**
   * Trigger library and song cache sync when WS reconnects,
   * so dirty metadata and songs are pushed/pulled after being offline.
   */
  useEffect(() => {
    if (!authStore.isAuthenticated) return;
    if (!setlistStore.isConnected) return;

    void setlistStore.library.sync();
    void songLibraryStore
      .syncLibrary()
      .catch((err) =>
        console.error("Failed to sync song library on reconnect:", err),
      );
  }, [
    authStore.isAuthenticated,
    setlistStore.isConnected,
    setlistStore,
    songLibraryStore,
  ]);

  const content = (
    <BridgeProvider>
      <MobXProviderContext.Provider value={stores}>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          disableTransitionOnChange
        >
          {isStandalonePage ? (
            <Component {...pageProps} />
          ) : (
            <AppLayout>
              <Component {...pageProps} />
            </AppLayout>
          )}
        </ThemeProvider>
      </MobXProviderContext.Provider>
    </BridgeProvider>
  );

  if (process.env.NODE_ENV === "development") return content;

  return <SerwistProvider swUrl="/serwist/sw.js">{content}</SerwistProvider>;
});

export default App;
